import React, { useEffect, useState, useContext } from 'react';
import { Button, Typography, Alert, Spin, Space, Modal } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { CheckCircleOutlined, CloseCircleOutlined, SyncOutlined, DisconnectOutlined, LinkOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { getGoogleCalendarV2Api } from "../../api/apiInstances";
import { GoogleCalendarConnectionStatus } from "../../api-client";
import logger from "../../utils/logger";
import AccessControl from '../common/AccessControl';
import { accessibilityRoles } from '../../utils/accessUtils';
import { AlertContext } from '../../context/AlertContext';
import { ALERT_DURATIONS } from '../../constants/alerts';

const { Title, Text, Paragraph } = Typography;

const GoogleCalendarSettings: React.FC = () => {
    const intl = useIntl();
    const googleCalendarApi = getGoogleCalendarV2Api();
    const {setSuccessMessage, setVisible, setError} = useContext(AlertContext);

    const [loading, setLoading] = useState(true);
    const [connectionStatus, setConnectionStatus] = useState<GoogleCalendarConnectionStatus | null>(null);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);

    useEffect(() => {
        loadConnectionStatus();
    }, []);

    const loadConnectionStatus = async () => {
        setLoading(true);
        try {
            const response = await googleCalendarApi.getGoogleCalendarConnectionStatusV2();
            setConnectionStatus(response.data);
        } catch (error) {
            logger.error('Failed to load Google Calendar connection status:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleConnect = async () => {
        setActionInProgress(true);
        try {
            const redirectUri = `${window.location.origin}/google-calendar-callback`;

            const response = await googleCalendarApi.initiateGoogleCalendarConnectionV2({
                redirectUri
            });

            const authUrl = response.data.authorizationUrl;

            // Try to open in popup first (better UX on desktop)
            const popup = window.open(
                authUrl,
                'google-oauth',
                'width=600,height=700,scrollbars=yes'
            );

            // Check if popup was blocked
            if (!popup || popup.closed || typeof popup.closed === 'undefined') {
                // Popup blocked - fallback to new tab
                window.open(authUrl, '_blank');
                // Start polling for connection status as fallback
                const pollInterval = setInterval(async () => {
                    try {
                        const status = await googleCalendarApi.getGoogleCalendarConnectionStatusV2();
                        if (status.data.connected) {
                            clearInterval(pollInterval);
                            setActionInProgress(false);
                            await loadConnectionStatus();

                            const successMsg = (
                                <>
                                    {intl.formatMessage({id: 'googleCalendarConnectedSuccess'})}
                                    {' '}
                                    <Link to="/activity-log" style={{ fontWeight: 'bold' }}>
                                        {intl.formatMessage({id: 'checkActivityLogForDetails'})}
                                    </Link>
                                </>
                            );
                            setSuccessMessage(successMsg);
                            setVisible(true);
                            setTimeout(() => {
                                setVisible(false);
                                setSuccessMessage('');
                            }, ALERT_DURATIONS.WITH_LINK);
                        }
                    } catch (e) {
                        // Continue polling
                    }
                }, 2000);

                // Stop polling after 5 minutes
                setTimeout(() => {
                    clearInterval(pollInterval);
                    setActionInProgress(false);
                }, 300000);
            } else {
                // Popup opened successfully - listen for postMessage
                const messageHandler = async (event: MessageEvent) => {
                    if (event.origin !== window.location.origin) return;
                    if (event.data.type !== 'google-calendar-oauth') return;

                    window.removeEventListener('message', messageHandler);
                    setActionInProgress(false);

                    if (event.data.success) {
                        await loadConnectionStatus();

                        const successMsg = (
                            <>
                                {intl.formatMessage({id: 'googleCalendarConnectedSuccess'})}
                                {' '}
                                <Link to="/activity-log" style={{ fontWeight: 'bold' }}>
                                    {intl.formatMessage({id: 'checkActivityLogForDetails'})}
                                </Link>
                            </>
                        );
                        setSuccessMessage(successMsg);
                        setVisible(true);
                        setTimeout(() => {
                            setVisible(false);
                            setSuccessMessage('');
                        }, ALERT_DURATIONS.WITH_LINK);
                    } else {
                        setError(event.data.error || intl.formatMessage({id: 'googleCalendarConnectionFailed'}));
                        setVisible(true);
                        setTimeout(() => {
                            setVisible(false);
                            setError('');
                        }, ALERT_DURATIONS.DEFAULT);
                    }

                    // Close popup if still open
                    if (popup && !popup.closed) {
                        popup.close();
                    }
                };

                window.addEventListener('message', messageHandler);

                // Fallback: if popup is closed manually, stop loading state
                const popupCheckInterval = setInterval(() => {
                    if (popup.closed) {
                        clearInterval(popupCheckInterval);
                        window.removeEventListener('message', messageHandler);
                        setActionInProgress(false);
                        loadConnectionStatus(); // Refresh in case connection succeeded
                    }
                }, 1000);
            }
        } catch (error: any) {
            logger.error('Failed to initiate connection:', error);
            setError(error.response?.data?.detail || intl.formatMessage({id: 'googleCalendarConnectionFailed'}));
            setVisible(true);
            setTimeout(() => {
                setVisible(false);
                setError('');
            }, ALERT_DURATIONS.DEFAULT);
            setActionInProgress(false);
        }
    };

    const handleDisconnect = async () => {
        setActionInProgress(true);
        setDisconnectModalOpen(false);
        try {
            await googleCalendarApi.disconnectGoogleCalendarV2();
            await loadConnectionStatus();

            setSuccessMessage(intl.formatMessage({id: 'googleCalendarDisconnected'}));
            setVisible(true);
            setTimeout(() => {
                setVisible(false);
                setSuccessMessage('');
            }, ALERT_DURATIONS.DEFAULT);
        } catch (error: any) {
            logger.error('Failed to disconnect:', error);
            setError(error.response?.data?.detail || 'Failed to disconnect Google Calendar');
            setVisible(true);
            setTimeout(() => {
                setVisible(false);
                setError('');
            }, ALERT_DURATIONS.DEFAULT);
        } finally {
            setActionInProgress(false);
        }
    };

    const handleSyncNow = async () => {
        setActionInProgress(true);
        try {
            const response = await googleCalendarApi.triggerGoogleCalendarSyncV2();
            const {eventsSynced, eventsFailed} = response.data;

            const syncMsg = (
                <>
                    {`${intl.formatMessage({id: 'syncCompleted'})} ${eventsSynced} ${intl.formatMessage({id: 'eventsSynced'})}, ${eventsFailed} ${intl.formatMessage({id: 'eventsFailed'})}`}
                    {' '}
                    <Link to="/activity-log" style={{ fontWeight: 'bold' }}>
                        {intl.formatMessage({id: 'checkActivityLogForDetails'})}
                    </Link>
                </>
            );

            setSuccessMessage(syncMsg);
            setVisible(true);
            setTimeout(() => {
                setVisible(false);
                setSuccessMessage('');
            }, ALERT_DURATIONS.WITH_LINK);

            await loadConnectionStatus();
        } catch (error: any) {
            logger.error('Failed to trigger sync:', error);
            setError(error.response?.data?.detail || 'Failed to trigger sync');
            setVisible(true);
            setTimeout(() => {
                setVisible(false);
                setError('');
            }, ALERT_DURATIONS.DEFAULT);
        } finally {
            setActionInProgress(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }

    const disconnectModalFooter = (
        <div className="modal-btn-wrapper">
            <Button className="modal-btn" onClick={() => setDisconnectModalOpen(false)}>
                <FormattedMessage id="cancel"/>
            </Button>
            <Button className="modal-btn" type="primary" danger onClick={handleDisconnect} loading={actionInProgress}>
                <FormattedMessage id="disconnect"/>
            </Button>
        </div>
    );

    return (
        <AccessControl accessibleFor={[accessibilityRoles.CLINIC_ADMIN]}>
            <div>
                <Title level={4}>
                    <FormattedMessage id="googleCalendar" />
                </Title>

                <Paragraph type="secondary">
                    <FormattedMessage id="googleCalendarDescription" />
                </Paragraph>

                {/* Connection Status */}
                <Space direction="vertical" size="middle">
                        {/* Status */}
                        <div>
                            <Text strong>
                                <FormattedMessage id="status" />:
                            </Text>{' '}
                            {connectionStatus?.connected ? (
                                <span>
                                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                    <Text type="success">
                                        <FormattedMessage id="connected" />
                                    </Text>
                                </span>
                            ) : (
                                <span>
                                    <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                                    <Text type="danger">
                                        <FormattedMessage id="notConnected" />
                                    </Text>
                                </span>
                            )}
                        </div>

                        {/* Google Account Email */}
                        {connectionStatus?.connected && connectionStatus.googleAccountEmail && (
                            <div>
                                <Text strong>
                                    <FormattedMessage id="googleAccount" />:
                                </Text>{' '}
                                <Text>{connectionStatus.googleAccountEmail}</Text>
                            </div>
                        )}

                        {/* Last Sync */}
                        {connectionStatus?.connected && connectionStatus.lastSyncAt && (
                            <div>
                                <Text strong>
                                    <FormattedMessage id="lastSync" />:
                                </Text>{' '}
                                <Text>{new Date(connectionStatus.lastSyncAt).toLocaleString()}</Text>
                            </div>
                        )}

                        {/* Error Message */}
                        {connectionStatus?.connected && connectionStatus.lastError && (
                            <Alert
                                message={<FormattedMessage id="syncError" />}
                                description={connectionStatus.lastError}
                                type="error"
                                showIcon
                            />
                        )}

                        {/* Actions */}
                        <div>
                            {!connectionStatus?.connected ? (
                                <Button
                                    type="primary"
                                    icon={<LinkOutlined className="mr-8" />}
                                    onClick={handleConnect}
                                    loading={actionInProgress}
                                    size="large"
                                >
                                    <FormattedMessage id="connectGoogleCalendar" />
                                </Button>
                            ) : (
                                <Space>
                                    <Button
                                        icon={<SyncOutlined className="mr-8" />}
                                        onClick={handleSyncNow}
                                        loading={actionInProgress}
                                    >
                                        <FormattedMessage id="syncNow" />
                                    </Button>
                                    <Button
                                        danger
                                        icon={<DisconnectOutlined className="mr-8" />}
                                        onClick={() => setDisconnectModalOpen(true)}
                                        loading={actionInProgress}
                                    >
                                        <FormattedMessage id="disconnect" />
                                    </Button>
                                </Space>
                            )}
                        </div>

                        {/* Instructions (shown below button when not connected) */}
                        {!connectionStatus?.connected && (
                            <Alert
                                message={<FormattedMessage id="howToConnect" />}
                                description={
                                    <ul>
                                        <li><FormattedMessage id="googleCalendarStep1" /></li>
                                        <li><FormattedMessage id="googleCalendarStep2" /></li>
                                        <li><FormattedMessage id="googleCalendarStep3" /></li>
                                    </ul>
                                }
                                type="info"
                                showIcon
                            />
                        )}
                    </Space>

                <Modal
                    transitionName=""
                    footer={disconnectModalFooter}
                    className="dentobook-modal"
                    title={<FormattedMessage id="disconnect"/>}
                    open={disconnectModalOpen}
                    onOk={handleDisconnect}
                    onCancel={() => setDisconnectModalOpen(false)}
                >
                    <h3 className="dentobook-content font-weight-500 font-24 text-align-center">
                        <FormattedMessage id="confirmDisconnectGoogleCalendar"/>
                    </h3>
                </Modal>
            </div>
        </AccessControl>
    );
};

export default GoogleCalendarSettings;
